// Contrail V3.4.0 — Admin → Notifications (V3.10.2: top half of Admin → Activity): what happened that admins should know about.
// Created server-side (database triggers + the cron), read state per admin, optional emails per type.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { Notice, Toolbar } from "./ui";

export type Notif = { id: number; at: string; type: string; severity: "info" | "warn" | "bad"; org_id: string | null; actor_email: string | null;
  title: string; body: string | null; link: string | null; entity?: string | null; entity_id?: string | null };

/** V3.12.0 (Kevin): where "Open" goes. Changes recorded in the audit log → Activity → System activity; tracker problems →
 *  Logs with that device already picked; everything else keeps the link the notification was created with. */
const TO_SYSTEM = new Set(["support_view", "company_edit", "user_created"]);
const TO_LOGS = new Set(["device_silent", "ingest_error", "unknown_tracker"]);
export function openLink(n: Pick<Notif, "type" | "link" | "entity" | "entity_id">): string | null {
  if (TO_SYSTEM.has(n.type)) return "/admin/activity?view=system";
  if (TO_LOGS.has(n.type)) {
    if (n.entity === "device" && n.entity_id) return `/admin/logs?device=${encodeURIComponent(n.entity_id)}`;
    if (n.type === "unknown_tracker" && n.entity_id) return `/admin/logs?unknown=${encodeURIComponent(n.entity_id)}`;
    return "/admin/logs";
  }
  return n.link;
}

export const NOTIF_TYPES: Record<string, string> = {
  first_login: "First sign-in", password_change: "Client changed password", company_edit: "Client edited company details",
  sim_added: "SIM card added", device_added: "Device added", user_created: "New user", demo_device: "Demo device created",
  unknown_tracker: "Unknown tracker sending", device_silent: "Device stopped reporting", ingest_error: "Platform data error",
  command_reply: "Tracker replied to a command", command_no_reply: "No reply to a command", support_view: "Support view opened",
};

const when = (iso: string) => {
  const m = (Date.now() - Date.parse(iso)) / 60000;
  if (m < 1) return "just now"; if (m < 60) return `${Math.round(m)} min ago`; if (m < 24 * 60) return `${Math.round(m / 60)} h ago`;
  return new Intl.DateTimeFormat("en-FJ", { timeZone: "Pacific/Fiji", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso));
};

/** Unread count for the Admin button / tab badge (refreshes every minute). */
export function useAdminUnread(enabled: boolean) {
  const [n, setN] = useState(0);
  const load = useCallback(async () => { const { data } = await supabase.rpc("admin_notif_unread"); setN(typeof data === "number" ? data : 0); }, []);
  useEffect(() => {
    if (!enabled) return;
    load();
    const t = setInterval(load, 60_000);
    const ev = () => load();
    window.addEventListener("fw-notif-read", ev);
    return () => { clearInterval(t); window.removeEventListener("fw-notif-read", ev); };
  }, [enabled, load]);
  return n;
}

export default function Notifications({ userId, embedded = false }: { userId: string; embedded?: boolean }) {
  const [rows, setRows] = useState<Notif[] | null>(null);
  const [read, setRead] = useState<Set<number>>(new Set());
  const [type, setType] = useState("");
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [emailTypes, setEmailTypes] = useState<string[]>([]);
  const [showPrefs, setShowPrefs] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const since = new Date(Date.now() - 30 * 864e5).toISOString();
    const [{ data: n }, { data: r }, { data: p }] = await Promise.all([
      supabase.from("admin_notifications").select("id, at, type, severity, org_id, actor_email, title, body, link, entity, entity_id").gte("at", since).order("at", { ascending: false }).limit(500),
      supabase.from("admin_notification_reads").select("notification_id").eq("user_id", userId),
      supabase.from("admin_notification_prefs").select("email_types").eq("user_id", userId).maybeSingle(),
    ]);
    setRows((n ?? []) as Notif[]);
    setRead(new Set((r ?? []).map((x: any) => x.notification_id)));
    setEmailTypes(p?.email_types ?? []);
  }, [userId]);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  async function markRead(ids: number[] | null) {
    await supabase.rpc("admin_notif_mark_read", { p_ids: ids });
    setRead((s) => new Set([...s, ...(ids ?? (rows ?? []).map((r) => r.id))]));
    window.dispatchEvent(new Event("fw-notif-read"));
  }
  async function savePrefs(types: string[]) {
    setEmailTypes(types);
    const { error } = await supabase.rpc("admin_notif_set_email", { p_types: types });
    setMsg(error ? error.message : types.length ? `You'll get an email for ${types.length} type${types.length === 1 ? "" : "s"}.` : "Emails turned off.");
  }

  const shown = useMemo(() => (rows ?? []).filter((r) => (!type || r.type === type) && (!onlyUnread || !read.has(r.id))), [rows, type, onlyUnread, read]);
  const unread = (rows ?? []).filter((r) => !read.has(r.id)).length;

  return (
    <div className={`notifs ${embedded ? "embedded" : ""}`}>
      <Toolbar>
        {embedded ? <h2>Notifications{unread ? <span className="badge">{unread > 99 ? "99+" : unread}</span> : null}</h2> : <h1>Notifications</h1>}
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">All types</option>
          {Object.entries(NOTIF_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="check"><input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} /> Unread only</label>
        <button className="btn-ghost" onClick={() => setShowPrefs(!showPrefs)} aria-expanded={showPrefs}>Email me…</button>
        <button className="btn-primary" disabled={!unread} onClick={() => markRead(null)}>Mark all read{unread ? ` (${unread})` : ""}</button>
      </Toolbar>
      {msg && <Notice onClose={() => setMsg(null)}>{msg}</Notice>}
      {showPrefs && (
        <section className="notif-prefs">
          <b>Email me when…</b>
          <div className="notif-prefs-grid">
            {Object.entries(NOTIF_TYPES).map(([k, v]) => (
              <label key={k} className="check"><input type="checkbox" checked={emailTypes.includes(k)}
                onChange={(e) => savePrefs(e.target.checked ? [...emailTypes, k] : emailTypes.filter((x) => x !== k))} /> {v}</label>
            ))}
          </div>
          <span className="sub">Emails go to your sign-in address within about a minute. Everything also stays listed here for 30 days.</span>
        </section>
      )}
      <ul className="notif-list" tabIndex={embedded ? 0 : undefined} aria-label="Notifications">
        {shown.map((n) => (
          <li key={n.id} className={`${read.has(n.id) ? "" : "unread"} sev-${n.severity}`}>
            <i className="notif-dot" aria-hidden="true" />
            <div className="notif-main">
              <div className="notif-top"><b>{n.title}</b><span className="notif-type">{NOTIF_TYPES[n.type] ?? n.type}</span></div>
              {n.body && <div className="notif-body">{n.body}</div>}
              <div className="notif-meta">{when(n.at)}{n.actor_email ? ` · ${n.actor_email}` : ""}</div>
            </div>
            <div className="notif-act">
              {openLink(n) && <Link className="btn-ghost small" to={openLink(n)!} onClick={() => !read.has(n.id) && markRead([n.id])}>Open</Link>}
              {!read.has(n.id) && <button className="btn-ghost small" onClick={() => markRead([n.id])}>Mark read</button>}
            </div>
          </li>
        ))}
        {rows && !shown.length && <li className="notif-empty">Nothing {onlyUnread ? "unread" : "here"} in the last 30 days.</li>}
        {!rows && <li className="notif-empty">Loading…</li>}
      </ul>
    </div>
  );
}
